import React, { useState, useEffect, useRef, useCallback, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation, Link, useParams } from 'react-router-dom';
import {
  HiOutlineHome,
  HiOutlineDocumentText,
  HiOutlineUser,
  HiOutlineUserGroup,
  HiOutlineCog,
  HiOutlineLogout,
  HiOutlineCalendar,
  HiOutlineChat,
  HiOutlineClipboardList,
  HiOutlineClipboardCheck,
  HiOutlineCube,
  HiOutlineKey,
  HiChevronLeft,
  HiChevronRight,
  HiChevronDown,
  HiOutlineChartBar,
  HiOutlineSparkles
} from 'react-icons/hi';
import ProfileModal from './components/ProfileModal';
import PushNotificationPrompt from './components/PushNotificationPrompt';
import ServiceTermsConsentScreen from './components/ServiceTermsConsentScreen';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { AlertProvider, useAlert } from './contexts/AlertContext';
import api from './config/axios';
import { API_ENDPOINTS } from './config/api';
import './App.css';
import { canAccessRoute, hasPermission } from './utils/permissions';
import { openFcherosWindow } from './utils/fcherosSession';
import {
  buildLoginUrlWithReturnTo,
  getReturnToFromSearch,
  resolvePostLoginPath,
} from './utils/returnTo';
import {
  notifyIncomingChatMessage,
  setActiveChatRoomId,
} from './services/chatBrowserNotification';
import {
  PUSH_NAVIGATE_EVENT,
  PUSH_REFRESH_STATE_KEY,
} from './utils/businessPushNavigation';
import { CHAT_UNREAD_REFRESH_EVENT } from './utils/chatUnreadCountRefresh';
import {
  addChatStompConnectionHandler,
  addChatStompPayloadHandler,
  disconnectChatStomp,
  ensureChatStompConnected,
  removeChatStompConnectionHandler,
  removeChatStompPayloadHandler,
} from './services/chatStompCoordinator';
// 라우트 단위 코드 스플리팅 (6단계)
const Login = lazy(() => import('./components/Login'));
const RealLogin = lazy(() => import('./components/RealLogin'));
const Register = lazy(() => import('./components/Register'));
const Home = lazy(() => import('./components/Home'));
const InvitationRegister = lazy(() => import('./components/InvitationRegister'));
const ConsentRequestPage = lazy(() => import('./components/ConsentRequestPage'));
const Dashboard = lazy(() => import('./components/Dashboard'));
const NoticeManagement = lazy(() => import('./components/NoticeManagement'));
const PromotionManagement = lazy(() => import('./components/PromotionManagement'));
const PromotionRatePolicyManagement = lazy(() => import('./components/PromotionRatePolicyManagement'));
const PromotionRead = lazy(() => import('./components/PromotionRead'));
const EventManagement = lazy(() => import('./components/EventManagement'));
const DesignRequestManagement = lazy(() => import('./components/DesignRequestManagement'));
const DesignRequestResponse = lazy(() => import('./components/DesignRequestResponse'));
const SubscriptionManagement = lazy(() => import('./components/SubscriptionManagement'));
const CustomerManagement = lazy(() => import('./components/CustomerManagement'));
const MemoManagement = lazy(() => import('./components/MemoManagement'));
const UserManagement = lazy(() => import('./components/UserManagement'));
const GaUserManagement = lazy(() => import('./components/GaUserManagement'));
const CompanyManagement = lazy(() => import('./components/CompanyManagement'));
const OrganizationManagement = lazy(() => import('./components/OrganizationManagement'));
const GaOrganizationManagement = lazy(() => import('./components/GaOrganizationManagement'));
const DivisionManagement = lazy(() => import('./components/DivisionManagement'));
const LoginManagement = lazy(() => import('./components/LoginManagement'));
const MemoSummaryCallLogManagement = lazy(() => import('./components/MemoSummaryCallLogManagement'));
const ResignationProcessingMonitor = lazy(() => import('./components/ResignationProcessingMonitor'));
const RegionManagement = lazy(() => import('./components/RegionManagement'));
const CompanyTypeRoleManagement = lazy(() => import('./components/CompanyTypeRoleManagement'));
const GaRoleManagement = lazy(() => import('./components/GaRoleManagement'));
const PermissionManagement = lazy(() => import('./components/PermissionManagement'));
const GroupMappingManagement = lazy(() => import('./components/GroupMappingManagement'));
const PartnerGroupManagement = lazy(() => import('./components/PartnerGroupManagement'));
const GAInsuUserManagement = lazy(() => import('./components/GAInsuUserManagement'));
const Chat = lazy(() => import('./components/Chat'));
const InvitationManagement = lazy(() => import('./components/InvitationManagement'));
const SystemSettings = lazy(() => import('./components/SystemSettings'));
const LlmSettings = lazy(() => import('./components/LlmSettings'));
const ReportStatisticsUser = lazy(() => import('./components/ReportStatisticsUser'));
const ReportStatisticsAll = lazy(() => import('./components/ReportStatisticsAll'));
const ReportStatisticsByReceiver = lazy(() => import('./components/ReportStatisticsByReceiver'));
const ReportStatisticsWaitingByInsurerPage = lazy(() => import('./components/ReportStatisticsWaitingByInsurerPage'));
const ReportStatisticsProcessingByInsurerPage = lazy(() => import('./components/ReportStatisticsProcessingByInsurerPage'));
const ReportStatisticsByGaReceiver = lazy(() => import('./components/ReportStatisticsByGaReceiver'));
const ReportStatisticsIsAll = lazy(() => import('./components/ReportStatisticsIsAll'));
const PersonalInfoPolicyManagement = lazy(() => import('./components/PersonalInfoPolicyManagement'));
const ServiceTermsManagement = lazy(() => import('./components/ServiceTermsManagement'));
const CodeManagement = lazy(() => import('./components/CodeManagement'));
const HolidayManagement = lazy(() => import('./components/HolidayManagement'));
const PrivacyPolicyPage = lazy(() => import('./components/PrivacyPolicyPage'));
const TermsOfServicePage = lazy(() => import('./components/TermsOfServicePage'));
const ImpersonateSession = lazy(() => import('./components/ImpersonateSession'));

/** 미로그인 시 /chat 등 허용 목록 밖 경로 → 로그인(returnTo) */
function UnauthenticatedProtectedRedirect() {
  const location = useLocation();
  return (
    <Navigate
      to={buildLoginUrlWithReturnTo(location.pathname, location.search)}
      replace
    />
  );
}

/** 로그인 성공 직후 URL이 /login?returnTo=… 인 채로 인증 라우트에 진입할 때 복귀 */
function PostLoginReturnRedirect() {
  const location = useLocation();
  return <Navigate to={resolvePostLoginPath(location.search)} replace />;
}

function DesignRequestSharedLinkRedirect() {
  const { id } = useParams();
  const { user } = useAuth();
  const [continueWeb, setContinueWeb] = useState(false);
  const [isMobileLink, setIsMobileLink] = useState(false);
  const attemptedRef = useRef(false);
  const designRequestId = Number(id);
  const appDeepLink = Number.isInteger(designRequestId) && designRequestId > 0
    ? `gopmobile://link/design-request/${designRequestId}?type=design-request&id=${designRequestId}`
    : '';
  const openAppDeepLink = useCallback(() => {
    if (!appDeepLink) return;
    window.location.href = appDeepLink;
  }, [appDeepLink]);

  useEffect(() => {
    if (attemptedRef.current) return undefined;
    attemptedRef.current = true;

    const isMobileDevice =
      /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        navigator.userAgent || ''
      );
    if (!isMobileDevice || !Number.isInteger(designRequestId) || designRequestId <= 0) {
      setContinueWeb(true);
      return undefined;
    }

    setIsMobileLink(true);
    const openTimer = window.setTimeout(openAppDeepLink, 100);
    return () => window.clearTimeout(openTimer);
  }, [designRequestId, openAppDeepLink]);

  if (!Number.isInteger(designRequestId) || designRequestId <= 0) {
    return <Navigate to="/dashboard" replace />;
  }
  if (isMobileLink && !continueWeb) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="w-full max-w-sm rounded-2xl bg-white p-6 text-center shadow-sm border border-gray-100">
          <div className="text-base font-semibold text-gray-900 mb-2">
            GOP 앱으로 열기
          </div>
          <p className="text-sm text-gray-600 leading-5 mb-5">
            앱이 설치되어 있으면 아래 버튼으로 설계요청 수신 화면을 열 수 있습니다.
          </p>
          <button
            type="button"
            onClick={openAppDeepLink}
            className="w-full rounded-lg bg-blue-600 px-4 py-3 text-sm font-semibold text-white hover:bg-blue-700"
          >
            앱으로 열기
          </button>
          <button
            type="button"
            onClick={() => setContinueWeb(true)}
            className="mt-3 w-full rounded-lg border border-gray-300 bg-white px-4 py-3 text-sm font-semibold text-gray-700 hover:bg-gray-50"
          >
            웹으로 계속 보기
          </button>
        </div>
      </div>
    );
  }
  if (!user) {
    return <UnauthenticatedProtectedRedirect />;
  }

  const permissions = Array.isArray(user?.permissions) ? user.permissions : [];
  const canOpenReceive =
    (user?.companyType === 'IS' || user?.companyType === 'GA') &&
    (
      permissions.includes('DESIGN_RECEIVE_CREATE') ||
      permissions.includes('DESIGN_RECEIVE_READ') ||
      permissions.includes('DESIGN_RECEIVE_READ_ALL')
    );
  if (canOpenReceive) {
    return <Navigate to={`/design-request-response?openDesignRequestId=${designRequestId}`} replace />;
  }

  const canOpenRequest =
    user?.companyType === 'GA' &&
    (permissions.includes('GA_DESIGN_CREATE') || permissions.includes('GA_DESIGN_READ'));
  if (canOpenRequest) {
    return <Navigate to={`/design-management?openDesignRequestId=${designRequestId}`} replace />;
  }

  return <Navigate to="/dashboard" replace />;
}

function SubscriptionRequestSharedLinkRedirect() {
  const { id } = useParams();
  const { user } = useAuth();
  const [continueWeb, setContinueWeb] = useState(false);
  const [isMobileLink, setIsMobileLink] = useState(false);
  const attemptedRef = useRef(false);
  const subscriptionRequestId = Number(id);
  const appDeepLink =
    Number.isInteger(subscriptionRequestId) && subscriptionRequestId > 0
      ? `gopmobile://link/subscription-request/${subscriptionRequestId}?type=subscription-request&id=${subscriptionRequestId}`
      : '';
  const openAppDeepLink = useCallback(() => {
    if (!appDeepLink) return;
    window.location.href = appDeepLink;
  }, [appDeepLink]);

  useEffect(() => {
    if (attemptedRef.current) return undefined;
    attemptedRef.current = true;

    const isMobileDevice =
      /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        navigator.userAgent || ''
      );
    if (
      !isMobileDevice ||
      !Number.isInteger(subscriptionRequestId) ||
      subscriptionRequestId <= 0
    ) {
      setContinueWeb(true);
      return undefined;
    }

    setIsMobileLink(true);
    const openTimer = window.setTimeout(openAppDeepLink, 100);
    return () => window.clearTimeout(openTimer);
  }, [subscriptionRequestId, openAppDeepLink]);

  if (!Number.isInteger(subscriptionRequestId) || subscriptionRequestId <= 0) {
    return <Navigate to="/dashboard" replace />;
  }
  if (isMobileLink && !continueWeb) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="w-full max-w-sm rounded-2xl bg-white p-6 text-center shadow-sm border border-gray-100">
          <div className="text-base font-semibold text-gray-900 mb-2">
            GOP 앱으로 열기
          </div>
          <p className="text-sm text-gray-600 leading-5 mb-5">
            앱이 설치되어 있으면 아래 버튼으로 청약요청 수신 화면을 열 수 있습니다.
          </p>
          <button
            type="button"
            onClick={openAppDeepLink}
            className="w-full rounded-lg bg-blue-600 px-4 py-3 text-sm font-semibold text-white hover:bg-blue-700"
          >
            앱으로 열기
          </button>
          <button
            type="button"
            onClick={() => setContinueWeb(true)}
            className="mt-3 w-full rounded-lg border border-gray-300 bg-white px-4 py-3 text-sm font-semibold text-gray-700 hover:bg-gray-50"
          >
            웹으로 계속 보기
          </button>
        </div>
      </div>
    );
  }
  if (!user) {
    return <UnauthenticatedProtectedRedirect />;
  }

  const permissions = Array.isArray(user?.permissions) ? user.permissions : [];
  const canOpenReceive =
    (user?.companyType === 'IS' || user?.companyType === 'GA') &&
    (
      permissions.includes('SUBSCRIPTION_RECEIVE_CREATE') ||
      permissions.includes('SUBSCRIPTION_RECEIVE_READ') ||
      permissions.includes('SUBSCRIPTION_RECEIVE_READ_ALL')
    );
  if (canOpenReceive) {
    return (
      <Navigate
        to={`/subscription-receive-management?openSubscriptionRequestId=${subscriptionRequestId}`}
        replace
      />
    );
  }

  const canOpenRequest =
    user?.companyType === 'GA' &&
    (
      permissions.includes('GA_SUBSCRIPTION_CREATE') ||
      permissions.includes('GA_SUBSCRIPTION_READ')
    );
  if (canOpenRequest) {
    return (
      <Navigate
        to={`/subscription-request-management?openSubscriptionRequestId=${subscriptionRequestId}`}
        replace
      />
    );
  }

  return <Navigate to="/dashboard" replace />;
}

function AppContent() {
  const { user, logout, updateUser, loading } = useAuth();
  const { showAlert } = useAlert();
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const [selectedKey, setSelectedKey] = useState('dashboard');
  const [profileModalVisible, setProfileModalVisible] = useState(false);
  const [profileDropdownOpen, setProfileDropdownOpen] = useState(false);
  const [openSubmenus, setOpenSubmenus] = useState({});
  const [unreadMessageCount, setUnreadMessageCount] = useState(0);
  const fetchUnreadMessageCountRef = useRef(null);
  const hasChatManagePermission = !!(
    user?.permissions &&
    Array.isArray(user.permissions) &&
    user.permissions.includes('CHAT_MANAGE')
  );

  // 읽지 않은 메시지 수 조회
  const fetchUnreadMessageCount = useCallback(async () => {
    if (
      !user ||
      !user.permissions ||
      !Array.isArray(user.permissions) ||
      !user.permissions.includes('CHAT_MANAGE')
    ) {
      return;
    }
    try {
      const response = await api.get(API_ENDPOINTS.CHAT_UNREAD_COUNT);
      setUnreadMessageCount(response.data.unreadCount || 0);
    } catch (error) {
      console.error('읽지 않은 메시지 수 조회 실패:', error);
    }
  }, [user]);

  useEffect(() => {
    fetchUnreadMessageCountRef.current = fetchUnreadMessageCount;
  }, [fetchUnreadMessageCount]);

  // WebSocket 연결 (user?.id·권한 기준으로만 재연결해 참조 변경 시 반복 연결 방지)
  useEffect(() => {
    if (!user?.id || !hasChatManagePermission) {
      return;
    }

    const userId = user.id;
    let fallbackIntervalId = null;

    // 초기 읽지 않은 메시지 수 조회
    const fetchCount = () => {
      const fn = fetchUnreadMessageCountRef.current;
      if (fn) fn();
    };

    const startFallback = () => {
      if (fallbackIntervalId !== null) return;
      fallbackIntervalId = setInterval(fetchCount, 30000);
    };

    const stopFallback = () => {
      if (fallbackIntervalId === null) return;
      clearInterval(fallbackIntervalId);
      fallbackIntervalId = null;
    };

    const handleConnectionState = (connected) => {
      if (connected) {
        stopFallback();
      } else {
        fetchCount();
        startFallback();
      }
    };

    const handleChatPayload = (payload) => {
      fetchCount();
      if (window.location.pathname === '/chat') {
        return;
      }
      if (!payload || payload.type === 'MESSAGES_READ' || payload.id == null || payload.senderId == null) {
        return;
      }
      notifyIncomingChatMessage(payload, userId);
    };

    fetchCount();
    addChatStompPayloadHandler(handleChatPayload);
    addChatStompConnectionHandler(handleConnectionState);
    ensureChatStompConnected(userId);

    return () => {
      removeChatStompPayloadHandler(handleChatPayload);
      removeChatStompConnectionHandler(handleConnectionState);
      stopFallback();
      disconnectChatStomp();
    };
  }, [user?.id, hasChatManagePermission]);

  // 채팅 페이지로 이동 시 읽지 않은 메시지 수 업데이트
  useEffect(() => {
    if (location.pathname === '/chat') {
      fetchUnreadMessageCount();
    } else {
      setActiveChatRoomId(null);
    }
  }, [location.pathname, fetchUnreadMessageCount]);

  // 채팅방 읽음 처리 직후 배지 갱신 (STOMP 미연결 시에도 즉시 반영)
  useEffect(() => {
    window.addEventListener(CHAT_UNREAD_REFRESH_EVENT, fetchUnreadMessageCount);
    return () => {
      window.removeEventListener(CHAT_UNREAD_REFRESH_EVENT, fetchUnreadMessageCount);
    };
  }, [fetchUnreadMessageCount]);

  // SW·포그라운드 알림 클릭: 이동 + 같은 화면이면 remount로 목록 재조회
  useEffect(() => {
    const go = (url) => {
      if (url == null || String(url).trim() === '') {
        return;
      }
      navigate(String(url), {
        state: { [PUSH_REFRESH_STATE_KEY]: Date.now() },
      });
    };
    const onSwMessage = (event) => {
      if (event.data?.type !== 'NOTIFICATION_CLICK') {
        return;
      }
      go(event.data.url);
    };
    const onPushNavigate = (event) => {
      go(event.detail?.url);
    };
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', onSwMessage);
    }
    window.addEventListener(PUSH_NAVIGATE_EVENT, onPushNavigate);
    return () => {
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.removeEventListener('message', onSwMessage);
      }
      window.removeEventListener(PUSH_NAVIGATE_EVENT, onPushNavigate);
    };
  }, [navigate]);

  // 운영사: 관리·시스템·프로모션 기본 펼침 / 원수사: 관리 기본 펼침
  useEffect(() => {
    if (!user) return;
    if (user.companyType === 'OP') {
      setOpenSubmenus({
        management: true,
        admin: true,
        promotion: true,
      });
    } else if (user.companyType === 'IS') {
      setOpenSubmenus((prev) => ({
        ...prev,
        management: true,
      }));
    }
  }, [user]);

  // URL에 따라 selectedKey 설정 및 초기 라우팅
  useEffect(() => {
    // 로딩 중이면 아무것도 하지 않음 (현재 경로 유지)
    if (loading) {
      return;
    }

    // 로딩이 완료되고 사용자가 없으면 로그인 페이지로 이동
    if (!user) {
      if (
        location.pathname !== '/' &&
        location.pathname !== '/login' &&
        location.pathname !== '/test-login' &&
        location.pathname !== '/register' &&
        location.pathname !== '/invitation-register' &&
        location.pathname !== '/privacy-policy' &&
        location.pathname !== '/terms-of-service' &&
        !location.pathname.startsWith('/consent-request/')
      ) {
        navigate(buildLoginUrlWithReturnTo(location.pathname, location.search), { replace: true });
      }
      return;
    }

    // 로그인 직후·세션 복원 시 returnTo 복귀 (FCM 채팅 딥링크 등)
    if (location.pathname === '/login') {
      const returnTo = getReturnToFromSearch(location.search);
      navigate(returnTo || '/dashboard', { replace: true });
      return;
    }

    // 사용자가 있고 루트 경로에 있으면 현황판로 이동
    if (location.pathname === '/') {
      navigate('/dashboard', { replace: true });
      return;
    }

    // 현재 경로에 맞는 selectedKey 설정
    const path = location.pathname;
    const keyMap = {
      '/': 'dashboard',
      '/dashboard': 'dashboard',
      '/events': 'events',
      '/notices': 'notices',
      '/promotion-management': 'promotion-manage',
      '/promotion-rate-management': 'promotion-rate-manage',
      '/promotion-read': 'promotion-read',
      '/design-management': 'design-management',
      '/subscription-request-management': 'subscription-request-management',
      '/subscription-receive-management': 'subscription-receive-management',
      '/customers': 'customers',
      '/memos': 'memos',
      '/personal-info-policies': 'personal-info-policies',
      '/service-terms-policies': 'service-terms-policies',
      '/users': 'users',
      '/companies': 'companies',
      '/organizations': 'organizations',
      '/ga-role-management': 'ga-role-management',
      '/role-management': 'role-management',
      '/permission-management': 'permission-management',
      '/divisions': 'divisions',
      '/login-logs': 'login-logs',
      '/memo-summary-call-logs': 'memo-summary-call-logs',
      '/resignation-processing-monitor': 'resignation-processing-monitor',
      '/system-settings': 'system-settings',
      '/llm-settings': 'llm-settings',
      '/regions': 'regions',
      '/invitation': 'invitation',
      '/design-request-response': 'design-request-response',
      '/group-mapping-management': 'group-mapping-management',
      '/partner-group-management': 'partner-group-management',
      '/ga-user-management': 'ga-user-management',
      '/ga-insu-user-management': 'ga-insu-user-management',
      '/chat': 'chat',
      '/report-statistics/user': 'report-statistics-user',
      '/report-statistics/all': 'report-statistics-all',
      '/report-statistics/by-receiver': 'report-statistics-by-receiver',
      '/report-statistics/by-receiver/waiting': 'report-statistics-by-receiver-waiting',
      '/report-statistics/by-receiver/processing': 'report-statistics-by-receiver-processing',
      '/report-statistics/by-ga-receiver': 'report-statistics-by-ga-receiver',
      '/report-statistics/is-admin': 'report-statistics-is-admin',
    };
    setSelectedKey(keyMap[path] || 'dashboard');

    if (path.startsWith('/promotion')) {
      setOpenSubmenus((prev) => ({ ...prev, promotion: true }));
    }
  }, [user, loading, location.pathname, location.search, navigate]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleProfileUpdate = (updatedUser) => {
    updateUser(updatedUser);
  };

  const toggleSubmenu = (key) => {
    setOpenSubmenus(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const getMenuItems = () => {
    const baseItems = [];

    // 현황판 메뉴: 회사 타입별 권한 기반으로 표시
    const hasDashboardPermission = user &&
      user.permissions &&
      Array.isArray(user.permissions) &&
      (
        // GA 타입: GA_DASHBOARD_SUPERVISOR, GA_DASHBOARD_TOPCLASS, GA_DASHBOARD_FC
        (user.companyType === 'GA' && (
          user.permissions.includes('GA_DASHBOARD_SUPERVISOR') ||
          user.permissions.includes('GA_DASHBOARD_TOPCLASS') ||
          user.permissions.includes('GA_DASHBOARD_FC')
        )) ||
        // IS 타입: IS_DASHBOARD_DIRECTOR, IS_DASHBOARD_MANAGER
        (user.companyType === 'IS' && (
          user.permissions.includes('IS_DASHBOARD_DIRECTOR') ||
          user.permissions.includes('IS_DASHBOARD_MANAGER')
        )) ||
        // OP 타입: OP_DASHBOARD_USER
        (user.companyType === 'OP' && user.permissions.includes('OP_DASHBOARD_USER'))
      );

    if (hasDashboardPermission) {
      baseItems.push({
        key: 'dashboard',
        icon: <HiOutlineHome className="w-5 h-5" />,
        label: '현황판',
      });
    }

    // 공지사항: 모든 로그인 사용자 조회 가능
    if (user) {
      baseItems.push({
        key: 'notices',
        icon: <HiOutlineDocumentText className="w-5 h-5" />,
        label: '공지사항',
      });
    }

    // GA 회원(개인설계사, GA매니저, GA디렉터) 메뉴: 권한 기반으로 메뉴 표시
    if (
      user &&
      user.companyType === 'GA' &&
      (Array.isArray(user.permissions) || user.isDeptLeader === true)
    ) {
      const userPermissions = Array.isArray(user.permissions) ? user.permissions : [];
      // 권한별 메뉴 정의 (순서대로 표시)
      const permissionMenuMap = [
        {
          key: 'events',
          icon: <HiOutlineCalendar className="w-5 h-5" />,
          label: '일정관리',
          requiredPermission: 'EVENT_MANAGE',
        },
        {
          key: 'memos',
          icon: <HiOutlineDocumentText className="w-5 h-5" />,
          label: '메모관리',
          requiredPermission: 'MEMO_MANAGE',
        },
        {
          key: 'customers',
          icon: <HiOutlineUserGroup className="w-5 h-5" />,
          label: '연락처관리',
          requiredPermission: 'CONTACT_MANAGE',
        },
        {
          key: 'design-management',
          icon: <HiOutlineClipboardList className="w-5 h-5" />,
          label: '설계요청관리',
          requiredPermission: 'GA_DESIGN_READ', // GA_DESIGN_CREATE 또는 GA_DESIGN_READ 권한 체크는 아래에서 처리
        },
        {
          key: 'design-request-response',
          icon: <HiOutlineClipboardList className="w-5 h-5" />,
          label: '설계수신관리',
          requiredPermission: 'DESIGN_RECEIVE_', // DESIGN_RECEIVE_CREATE 또는 DESIGN_RECEIVE_READ 권한 체크는 아래에서 처리
        },
        {
          key: 'subscription-request-management',
          icon: <HiOutlineClipboardCheck className="w-5 h-5" />,
          label: '청약요청관리',
          requiredPermission: 'GA_SUBSCRIPTION_READ', // GA_SUBSCRIPTION_CREATE 또는 GA_SUBSCRIPTION_READ 권한 체크는 아래에서 처리
        },
        {
          key: 'subscription-receive-management',
          icon: <HiOutlineClipboardCheck className="w-5 h-5" />,
          label: '청약수신관리',
          requiredPermission: 'SUBSCRIPTION_RECEIVE_READ', // SUBSCRIPTION_RECEIVE_CREATE 또는 SUBSCRIPTION_RECEIVE_READ 권한 체크는 아래에서 처리
        },
        {
          key: 'group-mapping-management',
          icon: <HiOutlineUserGroup className="w-5 h-5" />,
          label: '그룹매핑관리',
          requiredPermission: 'GA_GROUP_MAPPING_MANAGE',
        },
        {
          key: 'partner-group-management',
          icon: <HiOutlineUserGroup className="w-5 h-5" />,
          label: '매니저/총무그룹관리',
          requiredPermission: 'GA_PARTNER_GROUP_MANAGE',
        },
        {
          key: 'chat',
          icon: <HiOutlineChat className="w-5 h-5" />,
          label: '채팅',
          requiredPermission: 'CHAT_MANAGE',
          badge: unreadMessageCount > 0 ? unreadMessageCount : null,
        },
        {
          key: 'invitation',
          icon: <HiOutlineUserGroup className="w-5 h-5" />,
          label: '가입초청',
          requiredPermission: 'INVITE_MANAGE',
        },
        {
          key: 'ga-user-management',
          icon: <HiOutlineUser className="w-5 h-5" />,
          label: '사용자 관리',
          requiredPermission: 'USER_MANAGE',
        },
        {
          key: 'ga-insu-user-management',
          icon: <HiOutlineUserGroup className="w-5 h-5" />,
          label: '원수사매니저 관리',
          requiredPermission: 'GA_INSU_USER_MANAGE',
        },
        {
          key: 'organizations',
          icon: <HiOutlineUserGroup className="w-5 h-5" />,
          label: '부서관리',
          requiredPermission: 'GA_ORG_MANAGE',
        },
        {
          key: 'ga-role-management',
          icon: <HiOutlineKey className="w-5 h-5" />,
          label: '역할관리',
          requiredPermission: 'GA_ROLE_MANAGE',
        },
        {
          key: 'report-statistics-user',
          icon: <HiOutlineChartBar className="w-5 h-5" />,
          label: '리포트 통계(본인/하위)',
          requiredPermission: 'GA_REPORT_USER',
        },
        {
          key: 'report-statistics-all',
          icon: <HiOutlineChartBar className="w-5 h-5" />,
          label: '리포트 통계(전체)',
          requiredPermission: 'GA_REPORT_ALL',
        },
        {
          key: 'report-statistics-by-receiver',
          icon: <HiOutlineChartBar className="w-5 h-5" />,
          label: '리포트 통계(원수사별)',
          requiredPermission: 'GA_REPORT_ALL',
        },
        {
          key: 'report-statistics-by-ga-receiver',
          icon: <HiOutlineChartBar className="w-5 h-5" />,
          label: '리포트 통계(GA총무별)',
          requiredPermission: 'GA_REPORT_ALL',
        },
      ];

      // 권한이 있는 메뉴만 추가
      permissionMenuMap.forEach(menu => {
        // 설계요청관리 메뉴는 GA_DESIGN_CREATE 또는 GA_DESIGN_READ 권한이 있으면 표시
        if (menu.key === 'design-management') {
          if (userPermissions.includes('GA_DESIGN_CREATE') ||
              userPermissions.includes('GA_DESIGN_READ')) {
            baseItems.push({
              key: menu.key,
              icon: menu.icon,
              label: menu.label,
              badge: menu.badge,
            });
          }
        } else if (menu.key === 'design-request-response') {
          // 설계수신관리 메뉴는 DESIGN_RECEIVE_CREATE 또는 DESIGN_RECEIVE_READ 권한이 있으면 표시
          if (userPermissions.includes('DESIGN_RECEIVE_CREATE') ||
              userPermissions.includes('DESIGN_RECEIVE_READ') ||
              userPermissions.includes('DESIGN_RECEIVE_READ_ALL')) {
            baseItems.push({
              key: menu.key,
              icon: menu.icon,
              label: menu.label,
              badge: menu.badge,
            });
          }
        } else if (menu.key === 'subscription-request-management') {
          // 청약요청관리 메뉴는 GA_SUBSCRIPTION_CREATE 또는 GA_SUBSCRIPTION_READ 권한이 있으면 표시
          if (userPermissions.includes('GA_SUBSCRIPTION_CREATE') ||
              userPermissions.includes('GA_SUBSCRIPTION_READ')) {
            baseItems.push({
              key: menu.key,
              icon: menu.icon,
              label: menu.label,
              badge: menu.badge,
            });
          }
        } else if (menu.key === 'subscription-receive-management') {
          // 청약수신관리 메뉴는 SUBSCRIPTION_RECEIVE_CREATE 또는 SUBSCRIPTION_RECEIVE_READ 권한이 있으면 표시
          if (userPermissions.includes('SUBSCRIPTION_RECEIVE_CREATE') ||
              userPermissions.includes('SUBSCRIPTION_RECEIVE_READ')) {
            baseItems.push({
              key: menu.key,
              icon: menu.icon,
              label: menu.label,
              badge: menu.badge,
            });
          }
        } else if (menu.key === 'ga-insu-user-management') {
          if (hasPermission(user, 'GA_INSU_USER_MANAGE') || hasPermission(user, 'USER_MANAGE')) {
            baseItems.push({
              key: menu.key,
              icon: menu.icon,
              label: menu.label,
              badge: menu.badge,
            });
          }
        } else if (menu.key === 'partner-group-management') {
          if (canAccessRoute(user, 'partner-group-management')) {
            baseItems.push({
              key: menu.key,
              icon: menu.icon,
              label: menu.label,
              badge: menu.badge,
            });
          }
        } else if (menu.key === 'group-mapping-management') {
          if (canAccessRoute(user, 'group-mapping-management')) {
            baseItems.push({
              key: menu.key,
              icon: menu.icon,
              label: menu.label,
              badge: menu.badge,
            });
          }
        } else if (hasPermission(user, menu.requiredPermission)) {
          baseItems.push({
            key: menu.key,
            icon: menu.icon,
            label: menu.label,
            badge: menu.badge,
          });
        }
      });
    } else if (user && user.companyType === 'IS') {
      // IS 회원(원수사사용자, 원수사관리자) 메뉴: 권한 기반으로 메뉴 표시
      if (
        user.permissions &&
        Array.isArray(user.permissions)
      ) {
        // 권한별 메뉴 정의 (순서대로 표시)
        const permissionMenuMap = [
          {
            key: 'design-request-response',
            icon: <HiOutlineClipboardList className="w-5 h-5" />,
            label: '설계수신관리',
            requiredPermission: 'DESIGN_RECEIVE_READ', // DESIGN_RECEIVE_CREATE 또는 DESIGN_RECEIVE_READ 권한 체크는 아래에서 처리
          },
          {
            key: 'subscription-receive-management',
            icon: <HiOutlineClipboardCheck className="w-5 h-5" />,
            label: '청약수신관리',
            requiredPermission: 'SUBSCRIPTION_RECEIVE_READ', // SUBSCRIPTION_RECEIVE_CREATE 또는 SUBSCRIPTION_RECEIVE_READ 또는 SUBSCRIPTION_RECEIVE_READ_ALL 권한 체크는 아래에서 처리
          },
          {
            key: 'chat',
            icon: <HiOutlineChat className="w-5 h-5" />,
            label: '채팅',
            requiredPermission: 'CHAT_MANAGE',
            badge: unreadMessageCount > 0 ? unreadMessageCount : null,
          },
          {
            key: 'promotion-read',
            icon: <HiOutlineDocumentText className="w-5 h-5" />,
            label: '프로모션 조회',
            requiredPermission: null,
          },
          {
            key: 'report-statistics-is-admin',
            icon: <HiOutlineChartBar className="w-5 h-5" />,
            label: '리포트 통계(관리자용)',
            requiredPermission: 'IS_REPORT_ALL',
          },
        ];

        // 권한이 있는 메뉴만 추가
        permissionMenuMap.forEach(menu => {
          // 설계관리 메뉴는 DESIGN_RECEIVE_CREATE, DESIGN_RECEIVE_READ, 또는 DESIGN_RECEIVE_READ_ALL 권한이 있으면 표시
          if (menu.key === 'design-request-response') {
            const hasPermission = user.permissions.includes('DESIGN_RECEIVE_CREATE') ||
                user.permissions.includes('DESIGN_RECEIVE_READ') ||
                user.permissions.includes('DESIGN_RECEIVE_READ_ALL');
            if (hasPermission) {
              baseItems.push({
                key: menu.key,
                icon: menu.icon,
                label: menu.label,
                badge: menu.badge,
              });
            }
          } else if (menu.key === 'subscription-receive-management') {
            // 청약수신관리 메뉴는 SUBSCRIPTION_RECEIVE_CREATE, SUBSCRIPTION_RECEIVE_READ, 또는 SUBSCRIPTION_RECEIVE_READ_ALL 권한이 있으면 표시
            const hasPermission = user.permissions.includes('SUBSCRIPTION_RECEIVE_CREATE') ||
                user.permissions.includes('SUBSCRIPTION_RECEIVE_READ') ||
                user.permissions.includes('SUBSCRIPTION_RECEIVE_READ_ALL');
            if (hasPermission) {
              baseItems.push({
                key: menu.key,
                icon: menu.icon,
                label: menu.label,
                badge: menu.badge,
              });
            }
          } else if (menu.key === 'promotion-read') {
            if (canAccessRoute(user, 'promotion-read')) {
              baseItems.push({
                key: menu.key,
                icon: menu.icon,
                label: menu.label,
                badge: menu.badge,
              });
            }
          } else if (user.permissions.includes(menu.requiredPermission)) {
            baseItems.push({
              key: menu.key,
              icon: menu.icon,
              label: menu.label,
              badge: menu.badge,
            });
          }
        });
      }
    } else if (user && user.companyType === 'OP') {
      // OP 회원 메뉴: 권한 기반으로 메뉴 표시
      if (
        user.permissions &&
        Array.isArray(user.permissions)
      ) {
        // 권한별 메뉴 정의 (순서대로 표시)
        const permissionMenuMap = [
          {
            key: 'users',
            icon: <HiOutlineUser className="w-5 h-5" />,
            label: '사용자 관리',
            requiredPermission: 'USER_MANAGE',
          },
          {
            key: 'companies',
            icon: <HiOutlineUserGroup className="w-5 h-5" />,
            label: '원수사 관리',
            requiredPermission: 'COMPANY_MANAGE',
          },
          {
            key: 'organizations',
            icon: <HiOutlineUserGroup className="w-5 h-5" />,
            label: '운영조직 관리',
            requiredPermission: 'ORG_MANAGE',
          },
          {
            key: 'divisions',
            icon: <HiOutlineCube className="w-5 h-5" />,
            label: '사업장 관리',
            requiredPermission: null, // OP 회사 타입만 확인
          },
          {
            key: 'login-logs',
            icon: <HiOutlineKey className="w-5 h-5" />,
            label: '로그인 관리',
            requiredPermission: null, // OP 회사 타입만 확인
          },
          {
            key: 'memo-summary-call-logs',
            icon: <HiOutlineClipboardList className="w-5 h-5" />,
            label: 'STT/LLM 호출 기록',
            requiredPermission: null, // OP 회사 타입만 확인
          },
        ];

        // 권한이 있는 메뉴만 추가
        const managementChildren = [];
        permissionMenuMap.forEach(menu => {
          if (menu.requiredPermission === null) {
            // requiredPermission이 null이면 OP 회사 타입만 확인
            managementChildren.push({
              key: menu.key,
              icon: menu.icon,
              label: menu.label,
            });
          } else if (user.permissions.includes(menu.requiredPermission)) {
            managementChildren.push({
              key: menu.key,
              icon: menu.icon,
              label: menu.label,
            });
          }
        });

        // 하위 메뉴가 있을 때만 관리 메뉴 추가
        if (managementChildren.length > 0) {
          baseItems.push({
            key: 'management',
            icon: <HiOutlineUserGroup className="w-5 h-5" />,
            label: '관리',
            children: managementChildren,
          });
        }

        const promotionChildren = [];
        if (canAccessRoute(user, 'promotion-manage')) {
          promotionChildren.push({
            key: 'promotion-manage',
            icon: <HiOutlineDocumentText className="w-5 h-5" />,
            label: '프로모션 관리',
          });
        }
        if (canAccessRoute(user, 'promotion-rate-manage')) {
          promotionChildren.push({
            key: 'promotion-rate-manage',
            icon: <HiOutlineDocumentText className="w-5 h-5" />,
            label: '프로모션 가격관리',
          });
        }
        if (promotionChildren.length > 0) {
          baseItems.push({
            key: 'promotion',
            icon: <HiOutlineDocumentText className="w-5 h-5" />,
            label: '프로모션',
            children: promotionChildren,
          });
        }

        // 시스템 관리 메뉴: 권한 기반으로 메뉴 표시
        const systemMenuMap = [
          {
            key: 'system-settings',
            icon: <HiOutlineCog className="w-5 h-5" />,
            label: '배치 설정',
            requiredPermission: 'SYSTEM_SETTING',
          },
          {
            key: 'resignation-processing-monitor',
            icon: <HiOutlineClipboardList className="w-5 h-5" />,
            label: '(원수사)퇴사 배치',
            requiredPermission: null,
          },
          {
            key: 'llm-settings',
            icon: <HiOutlineCog className="w-5 h-5" />,
            label: 'LLM 설정',
            requiredPermission: null, // 권한 체크 없음
          },
          {
            key: 'regions',
            icon: <HiOutlineUserGroup className="w-5 h-5" />,
            label: '지역 관리',
            requiredPermission: 'REGION_MANAGE',
          },
          {
            key: 'role-management',
            icon: <HiOutlineUser className="w-5 h-5" />,
            label: '역할 관리',
            requiredPermission: 'ROLE_MANAGE',
          },
          {
            key: 'permission-management',
            icon: <HiOutlineCog className="w-5 h-5" />,
            label: '권한 관리',
            requiredPermission: 'PERMISSION_MANAGE',
          },
          {
            key: 'personal-info-policies',
            icon: <HiOutlineDocumentText className="w-5 h-5" />,
            label: '개인정보처리방침',
            requiredPermission: 'POLICY_MANAGE',
          },
          {
            key: 'service-terms-policies',
            icon: <HiOutlineClipboardList className="w-5 h-5" />,
            label: '서비스 이용약관',
            requiredPermission: null,
          },
          {
            key: 'code-management',
            icon: <HiOutlineClipboardList className="w-5 h-5" />,
            label: '코드 관리',
            requiredPermission: null,
          },
          {
            key: 'holiday-management',
            icon: <HiOutlineCalendar className="w-5 h-5" />,
            label: '휴일 관리',
            requiredPermission: null,
          },
        ];

        // 권한이 있는 메뉴만 추가
        const systemChildren = [];
        systemMenuMap.forEach(menu => {
          // LLM 설정 메뉴는 OP 회사 타입이기만 하면 표시
          if (menu.key === 'llm-settings' && user.companyType === 'OP') {
            systemChildren.push({
              key: menu.key,
              icon: menu.icon,
              label: menu.label,
            });
          } else if (menu.key === 'resignation-processing-monitor' && user.companyType === 'OP') {
            systemChildren.push({
              key: menu.key,
              icon: menu.icon,
              label: menu.label,
            });
          } else if (menu.key === 'service-terms-policies' && user.companyType === 'OP') {
            systemChildren.push({
              key: menu.key,
              icon: menu.icon,
              label: menu.label,
            });
          } else if (menu.key === 'code-management' && user.companyType === 'OP') {
            systemChildren.push({
              key: menu.key,
              icon: menu.icon,
              label: menu.label,
            });
          } else if (menu.key === 'holiday-management' && user.companyType === 'OP') {
            systemChildren.push({
              key: menu.key,
              icon: menu.icon,
              label: menu.label,
            });
          } else if (menu.requiredPermission && user.permissions.includes(menu.requiredPermission)) {
            systemChildren.push({
              key: menu.key,
              icon: menu.icon,
              label: menu.label,
            });
          }
        });

        // 하위 메뉴가 있을 때만 시스템 관리 메뉴 추가
        if (systemChildren.length > 0) {
          baseItems.push({
            key: 'admin',
            icon: <HiOutlineCog className="w-5 h-5" />,
            label: '시스템 관리',
            children: systemChildren,
          });
        }
      }
    }

    if (user && user.companyType === 'IS') {
      // IS 회원의 관리 메뉴: 권한 기반으로 메뉴 표시
      if (
        user.permissions &&
        Array.isArray(user.permissions)
      ) {
        const managementChildren = [];

        // 사용자 관리: USER_MANAGE 권한이 있으면 표시
        if (user.permissions.includes('USER_MANAGE')) {
          managementChildren.push({
            key: 'users',
            icon: <HiOutlineUser className="w-5 h-5" />,
            label: '사용자 관리',
          });
        }

        // 원수사 관리: COMPANY_MANAGE 권한이 있으면 표시
        if (user.permissions.includes('COMPANY_MANAGE')) {
          managementChildren.push({
            key: 'companies',
            icon: <HiOutlineUserGroup className="w-5 h-5" />,
            label: '원수사 관리',
          });
        }

        // 하위 메뉴가 있을 때만 관리 메뉴 추가
        if (managementChildren.length > 0) {
          baseItems.push({
            key: 'management',
            icon: <HiOutlineUserGroup className="w-5 h-5" />,
            label: '관리',
            children: managementChildren,
          });
        }

      }
    }

    return baseItems;
  };

  const handleMenuClick = (key) => {
    setSelectedKey(key);
    const pathMap = {
      'dashboard': '/dashboard',
      'events': '/events',
      'notices': '/notices',
      'promotion-manage': '/promotion-management',
      'promotion-rate-manage': '/promotion-rate-management',
      'promotion-read': '/promotion-read',
      'design-management': '/design-management',
      'subscription-request-management': '/subscription-request-management',
      'subscription-receive-management': '/subscription-receive-management',
      'customers': '/customers',
      'memos': '/memos',
      'personal-info-policies': '/personal-info-policies',
      'service-terms-policies': '/service-terms-policies',
      'users': '/users',
      'companies': '/companies',
      'organizations': '/organizations',
      'ga-role-management': '/ga-role-management',
      'divisions': '/divisions',
      'login-logs': '/login-logs',
      'memo-summary-call-logs': '/memo-summary-call-logs',
      'resignation-processing-monitor': '/resignation-processing-monitor',
      'system-settings': '/system-settings',
      'llm-settings': '/llm-settings',
      'regions': '/regions',
      'role-management': '/role-management',
      'permission-management': '/permission-management',
      'invitation': '/invitation',
      'ga-user-management': '/ga-user-management',
      'ga-insu-user-management': '/ga-insu-user-management',
      'design-request-response': '/design-request-response',
      'group-mapping-management': '/group-mapping-management',
      'partner-group-management': '/partner-group-management',
      'chat': '/chat',
      'report-statistics-user': '/report-statistics/user',
      'report-statistics-all': '/report-statistics/all',
      'report-statistics-by-receiver': '/report-statistics/by-receiver',
      'report-statistics-by-receiver-waiting': '/report-statistics/by-receiver/waiting',
      'report-statistics-by-receiver-processing': '/report-statistics/by-receiver/processing',
      'report-statistics-by-ga-receiver': '/report-statistics/by-ga-receiver',
      'report-statistics-is-admin': '/report-statistics/is-admin',
      'code-management': '/code-management',
      'holiday-management': '/holiday-management'
    };
    if (pathMap[key]) {
      navigate(pathMap[key]);
    }
  };

  // 로딩 중이면 로딩 화면 표시 (현재 경로 유지)
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-[#182370]"></div>
          <p className="mt-4 text-gray-600">로딩 중...</p>
        </div>
      </div>
    );
  }

  const RouteFallback = () => (
    <div className="flex items-center justify-center min-h-[280px]">
      <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-brand" />
    </div>
  );

  // 사용자가 없으면 로그인 페이지로
  if (!user) {
    return (
      <div className="min-h-screen flex flex-col">
        <div className="flex-1 flex flex-col min-h-0">
          <Suspense fallback={<RouteFallback />}>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/login" element={<RealLogin />} />
              <Route path="/test-login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/invitation-register" element={<InvitationRegister />} />
              <Route path="/consent-request/:token" element={<ConsentRequestPage />} />
              <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
              <Route path="/terms-of-service" element={<TermsOfServicePage />} />
              <Route path="/impersonate-session" element={<ImpersonateSession />} />
              <Route path="*" element={<UnauthenticatedProtectedRedirect />} />
            </Routes>
          </Suspense>
        </div>
        <footer className="border-t border-gray-200 bg-white py-4 px-6 text-center text-sm text-gray-600">
          <span className="text-gray-700">이지인스컴</span>
          <span className="mx-2">|</span>
          <Link to="/privacy-policy" className="text-blue-600 hover:text-blue-700 hover:underline">
            개인정보처리방침
          </Link>
        </footer>
      </div>
    );
  }

  if (
    user &&
    (user.companyType === 'GA' || user.companyType === 'IS') &&
    user.needsServiceTermsConsent === true
  ) {
    return <ServiceTermsConsentScreen />;
  }

  const pushRefreshAt = location.state?.[PUSH_REFRESH_STATE_KEY];

  return (
    <>
      <div className="flex min-h-screen bg-gray-50">
        {/* 사이드바 */}
        <div className={`bg-[#182370] text-white transition-all duration-300 flex flex-col ${
          collapsed ? 'w-20' : 'w-56'
        }`}>
          {/* 로고 */}
          <div className="h-16 flex items-center justify-center border-b border-gray-800 bg-white">
            <img
              src="/gop_ai_logo.png"
              alt="GOP AI"
              className={`transition-all object-contain ${collapsed ? 'h-8' : 'h-12'}`}
            />
          </div>

          {/* 메뉴 */}
          <div className="flex-1 overflow-y-auto py-4">
            <div className="space-y-1 px-2">
              {getMenuItems().map(item => {
                const isSelected = selectedKey === item.key || selectedKey.startsWith(item.key + '-');
                const hasChildren = item.children && item.children.length > 0;
                const isOpen = openSubmenus[item.key] || false;

                return (
                  <div key={item.key}>
                    <button
                      onClick={() => {
                        if (hasChildren) {
                          toggleSubmenu(item.key);
                        } else {
                          handleMenuClick(item.key);
                        }
                      }}
                      className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium rounded-md transition-colors relative ${
                        isSelected
                          ? 'bg-blue-600 text-white'
                          : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                      }`}
                    >
                      <div className="relative">
                        {item.icon}
                        {item.badge && item.badge > 0 && (
                          <span className={`absolute -top-1 -right-1 inline-flex items-center justify-center min-w-[18px] h-4.5 px-1 text-xs font-medium text-white bg-red-600 rounded-full ${
                            collapsed ? 'text-[10px] min-w-[16px] h-4' : 'hidden'
                          }`}>
                            {item.badge > 99 ? '99+' : item.badge}
                          </span>
                        )}
                      </div>
                      {!collapsed && <span className="flex-1 text-left">{item.label}</span>}
                      {!collapsed && item.badge && item.badge > 0 && (
                        <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-xs font-medium text-white bg-red-600 rounded-full">
                          {item.badge > 99 ? '99+' : item.badge}
                        </span>
                      )}
                      {!collapsed && hasChildren && (
                        <HiChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'transform rotate-180' : ''}`} />
                      )}
                    </button>
                    {!collapsed && hasChildren && isOpen && (
                      <div className="ml-4 mt-1 space-y-1">
                        {item.children.map(child => {
                          const isChildSelected = selectedKey === child.key;
                          return (
                            <button
                              key={child.key}
                              onClick={() => handleMenuClick(child.key)}
                              className={`w-full flex items-center gap-3 px-4 py-2 text-sm rounded-md transition-colors ${
                                isChildSelected
                                  ? 'bg-blue-600 text-white'
                                  : 'text-gray-400 hover:bg-gray-700 hover:text-white'
                              }`}
                            >
                              {child.icon}
                              <span>{child.label}</span>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {canAccessRoute(user, 'ga-assistant') && (
            <div className="border-t border-gray-800 p-2">
              <button
                type="button"
                onClick={() => {
                  if (!openFcherosWindow()) {
                    showAlert('GA비서 창을 열 수 없습니다. 브라우저에서 팝업을 허용해 주세요.');
                  }
                }}
                title="GA비서(베타)"
                className="w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium rounded-md transition-colors text-gray-300 hover:bg-gray-700 hover:text-white"
              >
                <HiOutlineSparkles className="w-5 h-5" />
                {!collapsed && <span className="flex-1 text-left">GA비서(베타)</span>}
              </button>
            </div>
          )}

          {/* 접기/펼치기 버튼 */}
          <div className="border-t border-gray-800 p-2">
            <button
              onClick={() => setCollapsed(!collapsed)}
              className="w-full flex items-center justify-center gap-2 px-4 py-2 text-sm text-gray-300 hover:bg-gray-700 rounded-md transition-colors"
            >
              {collapsed ? <HiChevronRight className="w-5 h-5" /> : <HiChevronLeft className="w-5 h-5" />}
              {!collapsed && <span>접기</span>}
            </button>
          </div>
        </div>

        {/* 메인 컨텐츠 영역 */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* 헤더  users: IS/OP 회사 타입 사용자용, ga-user-management: GA 회사 타입 사용자용 */}
          <header className="bg-white border-b border-gray-200 px-6 h-16 flex items-center justify-between shadow-sm">
            <h1 className="text-xl font-semibold text-gray-900">
              {selectedKey === 'dashboard' && '현황판'}
              {selectedKey === 'notices' && '공지사항'}
              {selectedKey === 'promotion-manage' && '프로모션 관리'}
              {selectedKey === 'promotion-rate-manage' && '프로모션 가격관리'}
              {selectedKey === 'promotion-read' && '프로모션 조회'}
              {selectedKey === 'events' && '일정관리'}
              {selectedKey === 'design-management' && '설계요청관리'}
              {selectedKey === 'subscription-request-management' && '청약요청관리'}
              {selectedKey === 'subscription-receive-management' && '청약수신관리'}
              {selectedKey === 'customers' && '연락처관리'}
              {selectedKey === 'memos' && '메모관리'}
              {selectedKey === 'personal-info-policies' && '개인정보처리방침'}
              {selectedKey === 'service-terms-policies' && '서비스 이용약관'}
              {selectedKey === 'users' && '사용자 관리'}
              {selectedKey === 'ga-user-management' && '사용자 관리'}
              {selectedKey === 'ga-insu-user-management' && '원수사 매니저관리'}
              {selectedKey === 'invitation' && '가입초청'}
              {selectedKey === 'companies' && '원수사 관리'}
              {selectedKey === 'organizations' && (user?.companyType === 'GA' ? '부서관리' : '운영조직 관리')}
              {selectedKey === 'ga-role-management' && '역할관리'}
              {selectedKey === 'divisions' && '사업장 관리'}
              {selectedKey === 'login-logs' && '로그인 관리'}
              {selectedKey === 'memo-summary-call-logs' && 'STT/LLM 호출 기록'}
              {selectedKey === 'resignation-processing-monitor' && '(원수사)퇴사 배치'}
              {selectedKey === 'system-settings' && '배치 설정'}
              {selectedKey === 'llm-settings' && 'LLM 설정'}
              {selectedKey === 'regions' && '지역 관리'}
              {selectedKey === 'role-management' && '역할 관리'}
              {selectedKey === 'permission-management' && '권한 관리'}
              {selectedKey === 'design-request-response' && '설계수신관리'}
              {selectedKey === 'group-mapping-management' && '그룹매핑관리'}
              {selectedKey === 'partner-group-management' && '매니저/총무그룹관리'}
              {selectedKey === 'chat' && '채팅'}
            </h1>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2 text-sm text-gray-700">
                <span>안녕하세요, {user.name}님</span>
                {user.companyName && (
                  <span className="text-gray-500">
                    ({user.companyType === 'GA' && user.divisionName
                      ? `${user.companyName} (${user.divisionName})`
                      : user.companyName})
                  </span>
                )}
              </div>
              <div className="relative">
                <button
                  onClick={() => setProfileDropdownOpen(!profileDropdownOpen)}
                  className="w-10 h-10 rounded-full bg-[#182370] text-white flex items-center justify-center hover:bg-[#1a2a7a] transition-colors cursor-pointer"
                >
                  <HiOutlineUser className="w-5 h-5" />
                </button>
                {profileDropdownOpen && (
                  <>
                    <div
                      className="fixed inset-0 z-10"
                      onClick={() => setProfileDropdownOpen(false)}
                    ></div>
                    <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg border border-gray-200 z-20">
                      <div className="py-1">
                        <button
                          onClick={() => {
                            setProfileModalVisible(true);
                            setProfileDropdownOpen(false);
                          }}
                          className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                        >
                          <HiOutlineUser className="w-4 h-4" />
                          프로필 보기
                        </button>
                        <button
                          onClick={() => {
                            handleLogout();
                            setProfileDropdownOpen(false);
                          }}
                          className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                        >
                          <HiOutlineLogout className="w-4 h-4" />
                          로그아웃
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </header>

          <PushNotificationPrompt />

          {/* 컨텐츠 */}
          <main className="flex-1 overflow-y-auto bg-gray-50 p-6">
            <Suspense fallback={<RouteFallback />}>
            {/* pushRefreshAt 변경 시 remount → 이미 같은 화면이어도 목록 재조회 */}
            <Routes key={pushRefreshAt != null ? `push-${pushRefreshAt}` : 'app-routes'}>
              <Route path="/impersonate-session" element={<ImpersonateSession />} />
              <Route path="/" element={
                user &&
                user.permissions &&
                Array.isArray(user.permissions) &&
                (
                  (user.companyType === 'GA' && (
                    user.permissions.includes('GA_DASHBOARD_SUPERVISOR') ||
                    user.permissions.includes('GA_DASHBOARD_TOPCLASS') ||
                    user.permissions.includes('GA_DASHBOARD_FC')
                  )) ||
                  (user.companyType === 'IS' && (
                    user.permissions.includes('IS_DASHBOARD_DIRECTOR') ||
                    user.permissions.includes('IS_DASHBOARD_MANAGER')
                  )) ||
                  (user.companyType === 'OP' && user.permissions.includes('OP_DASHBOARD_USER'))
                ) ?
                <Navigate to="/dashboard" replace /> :
                user ?
                <Navigate to="/dashboard" replace /> :
                <Navigate to="/login" replace />
              } />
              <Route path="/dashboard" element={
                user &&
                user.permissions &&
                Array.isArray(user.permissions) &&
                (
                  (user.companyType === 'GA' && (
                    user.permissions.includes('GA_DASHBOARD_SUPERVISOR') ||
                    user.permissions.includes('GA_DASHBOARD_TOPCLASS') ||
                    user.permissions.includes('GA_DASHBOARD_FC')
                  )) ||
                  (user.companyType === 'IS' && (
                    user.permissions.includes('IS_DASHBOARD_DIRECTOR') ||
                    user.permissions.includes('IS_DASHBOARD_MANAGER')
                  )) ||
                  (user.companyType === 'OP' && user.permissions.includes('OP_DASHBOARD_USER'))
                ) ?
                <Dashboard /> :
                <div className="flex items-center justify-center h-screen">
                  <div className="text-center">
                    <h2 className="text-2xl font-bold text-gray-800 mb-4">접근 권한이 없습니다</h2>
                    <p className="text-gray-600 mb-4">현황판에 접근할 수 있는 권한이 없습니다.</p>
                    <button
                      onClick={async () => {
                        await logout();
                        window.location.href = '/login';
                      }}
                      className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                    >
                      로그아웃
                    </button>
                  </div>
                </div>
              } />
              <Route path="/events" element={
                user &&
                user.companyType === 'GA' &&
                user.permissions &&
                Array.isArray(user.permissions) &&
                user.permissions.includes('EVENT_MANAGE') ?
                <EventManagement /> :
                <Navigate to="/dashboard" replace />
              } />
              <Route path="/notices" element={user ? <NoticeManagement /> : <Navigate to="/login" replace />} />
              <Route path="/promotion-management" element={
                user && canAccessRoute(user, 'promotion-manage') ?
                <PromotionManagement /> :
                <Navigate to="/dashboard" replace />
              } />
              <Route path="/promotion-rate-management" element={
                user && canAccessRoute(user, 'promotion-rate-manage') ?
                <PromotionRatePolicyManagement /> :
                <Navigate to="/dashboard" replace />
              } />
              <Route path="/promotion-read" element={
                user && canAccessRoute(user, 'promotion-read') ?
                <PromotionRead /> :
                <Navigate to="/dashboard" replace />
              } />
              <Route path="/link/design-request/:id" element={
                <DesignRequestSharedLinkRedirect />
              } />
              <Route path="/link/subscription-request/:id" element={
                <SubscriptionRequestSharedLinkRedirect />
              } />
              <Route path="/design-management" element={
                user &&
                user.companyType === 'GA' &&
                user.permissions &&
                Array.isArray(user.permissions) &&
                (user.permissions.includes('GA_DESIGN_CREATE') ||
                 user.permissions.includes('GA_DESIGN_READ')) ?
                <DesignRequestManagement /> :
                <Navigate to="/dashboard" replace />
              } />
              <Route path="/subscription-request-management" element={
                user &&
                user.companyType === 'GA' &&
                user.permissions &&
                Array.isArray(user.permissions) &&
                (user.permissions.includes('GA_SUBSCRIPTION_CREATE') ||
                 user.permissions.includes('GA_SUBSCRIPTION_READ')) ?
                <SubscriptionManagement /> :
                <Navigate to="/dashboard" replace />
              } />
              <Route path="/subscription-receive-management" element={
                user && (
                  (user.companyType === 'GA' && user.permissions && Array.isArray(user.permissions) &&
                   (user.permissions.includes('SUBSCRIPTION_RECEIVE_CREATE') ||
                    user.permissions.includes('SUBSCRIPTION_RECEIVE_READ'))) ||
                  (user.companyType === 'IS' && user.permissions && Array.isArray(user.permissions) &&
                   (user.permissions.includes('SUBSCRIPTION_RECEIVE_CREATE') ||
                    user.permissions.includes('SUBSCRIPTION_RECEIVE_READ') ||
                    user.permissions.includes('SUBSCRIPTION_RECEIVE_READ_ALL')))
                ) ?
                <SubscriptionManagement /> :
                <Navigate to="/dashboard" replace />
              } />
              <Route path="/customers" element={
                user &&
                user.companyType === 'GA' &&
                user.permissions &&
                Array.isArray(user.permissions) &&
                user.permissions.includes('CONTACT_MANAGE') ?
                <CustomerManagement /> :
                <Navigate to="/dashboard" replace />
              } />
              <Route path="/memos" element={
                user &&
                user.companyType === 'GA' &&
                user.permissions &&
                Array.isArray(user.permissions) &&
                user.permissions.includes('MEMO_MANAGE') ?
                <MemoManagement /> :
                <Navigate to="/dashboard" replace />
              } />
              <Route path="/personal-info-policies" element={
                user &&
                user.companyType === 'OP' &&
                user.permissions &&
                Array.isArray(user.permissions) &&
                user.permissions.includes('POLICY_MANAGE') ?
                <PersonalInfoPolicyManagement /> :
                <Navigate to="/dashboard" replace />
              } />
              <Route path="/service-terms-policies" element={
                user && user.companyType === 'OP' ?
                <ServiceTermsManagement /> :
                <Navigate to="/dashboard" replace />
              } />
              <Route path="/users" element={
                user && (
                  (user.companyType === 'IS' && user.permissions && Array.isArray(user.permissions) && user.permissions.includes('USER_MANAGE')) ||
                  (user.companyType === 'OP' && user.permissions && Array.isArray(user.permissions) && user.permissions.includes('USER_MANAGE'))
                ) ?
                <UserManagement /> :
                <Navigate to="/dashboard" replace />
              } />
              <Route path="/ga-user-management" element={
                user &&
                user.companyType === 'GA' &&
                user.permissions &&
                Array.isArray(user.permissions) &&
                user.permissions.includes('USER_MANAGE') ?
                <GaUserManagement /> :
                <Navigate to="/dashboard" replace />
              } />
              <Route path="/ga-insu-user-management" element={
                user &&
                user.companyType === 'GA' &&
                user.permissions &&
                Array.isArray(user.permissions) &&
                (user.permissions.includes('GA_INSU_USER_MANAGE') || user.permissions.includes('USER_MANAGE')) ?
                <GAInsuUserManagement /> :
                <Navigate to="/dashboard" replace />
              } />
              <Route path="/companies" element={
                user && (
                  (user.companyType === 'IS' && user.permissions && Array.isArray(user.permissions) && user.permissions.includes('COMPANY_MANAGE')) ||
                  (user.companyType === 'OP' && user.permissions && Array.isArray(user.permissions) && user.permissions.includes('COMPANY_MANAGE'))
                ) ?
                <CompanyManagement /> :
                <Navigate to="/dashboard" replace />
              } />
              <Route path="/organizations" element={
                user &&
                user.permissions &&
                Array.isArray(user.permissions) &&
                ((user.companyType === 'OP' && user.permissions.includes('ORG_MANAGE')) ||
                 (user.companyType === 'GA' && user.permissions.includes('GA_ORG_MANAGE'))) ?
                (user.companyType === 'GA' ? <GaOrganizationManagement /> : <OrganizationManagement />) :
                <Navigate to="/dashboard" replace />
              } />
              <Route path="/divisions" element={
                user && user.companyType === 'OP' ?
                <DivisionManagement /> :
                <Navigate to="/dashboard" replace />
              } />
              <Route path="/login-logs" element={
                user && user.companyType === 'OP' ?
                <LoginManagement /> :
                <Navigate to="/dashboard" replace />
              } />
              <Route path="/memo-summary-call-logs" element={
                user && user.companyType === 'OP' ?
                <MemoSummaryCallLogManagement /> :
                <Navigate to="/dashboard" replace />
              } />
              <Route path="/resignation-processing-monitor" element={
                user && user.companyType === 'OP' ?
                <ResignationProcessingMonitor /> :
                <Navigate to="/dashboard" replace />
              } />
              <Route path="/regions" element={
                user &&
                user.companyType === 'OP' &&
                user.permissions &&
                Array.isArray(user.permissions) &&
                user.permissions.includes('REGION_MANAGE') ?
                <RegionManagement /> :
                <Navigate to="/dashboard" replace />
              } />
              <Route path="/role-management" element={
                user &&
                user.companyType === 'OP' &&
                user.permissions &&
                Array.isArray(user.permissions) &&
                user.permissions.includes('ROLE_MANAGE') ?
                <CompanyTypeRoleManagement /> :
                <Navigate to="/dashboard" replace />
              } />
              <Route path="/ga-role-management" element={
                user &&
                user.companyType === 'GA' &&
                user.permissions &&
                Array.isArray(user.permissions) &&
                user.permissions.includes('GA_ROLE_MANAGE') ?
                <GaRoleManagement /> :
                <Navigate to="/dashboard" replace />
              } />
              <Route path="/permission-management" element={
                user &&
                user.companyType === 'OP' &&
                user.permissions &&
                Array.isArray(user.permissions) &&
                user.permissions.includes('PERMISSION_MANAGE') ?
                <PermissionManagement /> :
                <Navigate to="/dashboard" replace />
              } />
              <Route path="/design-request-response" element={
                user &&
                user.permissions &&
                Array.isArray(user.permissions) &&
                ((user.companyType === 'IS' &&
                  (user.permissions.includes('DESIGN_RECEIVE_CREATE') ||
                   user.permissions.includes('DESIGN_RECEIVE_READ') ||
                   user.permissions.includes('DESIGN_RECEIVE_READ_ALL'))) ||
                 (user.companyType === 'GA' &&
                  (user.permissions.includes('DESIGN_RECEIVE_CREATE') ||
                   user.permissions.includes('DESIGN_RECEIVE_READ')))) ?
                <DesignRequestResponse /> :
                <Navigate to="/dashboard" replace />
              } />
              <Route path="/group-mapping-management" element={
                user && canAccessRoute(user, 'group-mapping-management') ?
                <GroupMappingManagement /> :
                <Navigate to="/dashboard" replace />
              } />
              <Route path="/partner-group-management" element={
                user && canAccessRoute(user, 'partner-group-management') ?
                <PartnerGroupManagement /> :
                <Navigate to="/dashboard" replace />
              } />
              <Route path="/chat" element={
                user &&
                user.permissions &&
                Array.isArray(user.permissions) &&
                user.permissions.includes('CHAT_MANAGE') ?
                <Chat /> :
                <Navigate to="/dashboard" replace />
              } />
              <Route path="/invitation" element={
                user &&
                user.companyType === 'GA' &&
                user.permissions &&
                Array.isArray(user.permissions) &&
                user.permissions.includes('INVITE_MANAGE') ?
                <InvitationManagement /> :
                <Navigate to="/dashboard" replace />
              } />
              <Route path="/report-statistics/user" element={
                user &&
                user.companyType === 'GA' &&
                user.permissions &&
                Array.isArray(user.permissions) &&
                user.permissions.includes('GA_REPORT_USER') ?
                <ReportStatisticsUser /> :
                <Navigate to="/dashboard" replace />
              } />
              <Route path="/report-statistics/all" element={
                user &&
                user.companyType === 'GA' &&
                user.permissions &&
                Array.isArray(user.permissions) &&
                user.permissions.includes('GA_REPORT_ALL') ?
                <ReportStatisticsAll /> :
                <Navigate to="/dashboard" replace />
              } />
              <Route path="/report-statistics/by-receiver" element={
                user &&
                user.companyType === 'GA' &&
                user.permissions &&
                Array.isArray(user.permissions) &&
                user.permissions.includes('GA_REPORT_ALL') ?
                <ReportStatisticsByReceiver /> :
                <Navigate to="/dashboard" replace />
              } />
              <Route path="/report-statistics/by-receiver/waiting" element={
                user &&
                user.companyType === 'GA' &&
                user.permissions &&
                Array.isArray(user.permissions) &&
                user.permissions.includes('GA_REPORT_ALL') ?
                <ReportStatisticsWaitingByInsurerPage /> :
                <Navigate to="/dashboard" replace />
              } />
              <Route path="/report-statistics/by-receiver/processing" element={
                user &&
                user.companyType === 'GA' &&
                user.permissions &&
                Array.isArray(user.permissions) &&
                user.permissions.includes('GA_REPORT_ALL') ?
                <ReportStatisticsProcessingByInsurerPage /> :
                <Navigate to="/dashboard" replace />
              } />
              <Route path="/report-statistics/by-ga-receiver" element={
                user &&
                user.companyType === 'GA' &&
                user.permissions &&
                Array.isArray(user.permissions) &&
                user.permissions.includes('GA_REPORT_ALL') ?
                <ReportStatisticsByGaReceiver /> :
                <Navigate to="/dashboard" replace />
              } />
              <Route path="/report-statistics/is-admin" element={
                user &&
                user.companyType === 'IS' &&
                user.permissions &&
                Array.isArray(user.permissions) &&
                user.permissions.includes('IS_REPORT_ALL') ?
                <ReportStatisticsIsAll /> :
                <Navigate to="/dashboard" replace />
              } />
              <Route path="/system-settings" element={
                user &&
                user.companyType === 'OP' &&
                user.permissions &&
                Array.isArray(user.permissions) &&
                user.permissions.includes('SYSTEM_SETTING') ?
                <SystemSettings /> :
                <Navigate to="/dashboard" replace />
              } />
              <Route path="/llm-settings" element={
                user &&
                user.companyType === 'OP' ?
                <LlmSettings /> :
                <Navigate to="/dashboard" replace />
              } />
              <Route path="/code-management" element={
                user &&
                user.companyType === 'OP' ?
                <CodeManagement /> :
                <Navigate to="/dashboard" replace />
              } />
              <Route path="/holiday-management" element={
                user &&
                user.companyType === 'OP' ?
                <HolidayManagement /> :
                <Navigate to="/dashboard" replace />
              } />
              <Route path="/terms-of-service" element={<TermsOfServicePage />} />
              <Route path="/login" element={<PostLoginReturnRedirect />} />
              <Route path="*" element={
                user &&
                user.permissions &&
                Array.isArray(user.permissions) &&
                (
                  (user.companyType === 'GA' && (
                    user.permissions.includes('GA_DASHBOARD_SUPERVISOR') ||
                    user.permissions.includes('GA_DASHBOARD_TOPCLASS') ||
                    user.permissions.includes('GA_DASHBOARD_FC')
                  )) ||
                  (user.companyType === 'IS' && (
                    user.permissions.includes('IS_DASHBOARD_DIRECTOR') ||
                    user.permissions.includes('IS_DASHBOARD_MANAGER')
                  )) ||
                  (user.companyType === 'OP' && user.permissions.includes('OP_DASHBOARD_USER'))
                ) ?
                <Navigate to="/dashboard" replace /> :
                <Navigate to="/login" replace />
              } />
            </Routes>
            </Suspense>
          </main>
        </div>
      </div>

      <ProfileModal
        visible={profileModalVisible}
        onClose={() => setProfileModalVisible(false)}
        user={user}
        onUpdate={handleProfileUpdate}
      />
    </>
  );
}

function App() {
  return (
    <AuthProvider>
      <AlertProvider>
        <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
          <AppContent />
        </Router>
      </AlertProvider>
    </AuthProvider>
  );
}

export default App;
